import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TablePagination,
  Checkbox,
  ListItemText,
  Menu,
  ListItemIcon,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Restore as RestoreIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  HelpOutline as HelpOutlineIcon,
  PinDrop
} from '@mui/icons-material';
import { customerService, Customer, CustomerSearchDto, CreateCustomerDto, UpdateCustomerDto, CustomerSource, CustomerStatus } from '../services/customerService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { indexedDbService } from '../services/indexedDbService';
import BaseDialog from '../components/common/BaseDialog';
import GoogleMapsAutocomplete from '../components/common/GoogleMapsAutocomplete';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import JobForm from '../components/common/JobForm';
import CustomerFormDialog from '../components/common/CustomerFormDialog';
import { businessService, Business } from '../services/businessService';
import { jobService, CreateJobDto, JobType, Priority } from '../services/jobService';
import { Helmet } from 'react-helmet-async';

const CustomersPage: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useBusinessTheme();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());
  const [searchDto, setSearchDto] = useState<CustomerSearchDto>({
    page: 1,
    pageSize: 20
  });
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [mainSearchTimeout, setMainSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<CustomerStatus[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<CreateCustomerDto>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    source: CustomerSource.Other
  });
  
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [addressCoordinates, setAddressCoordinates] = useState<{ lat?: number; lng?: number }>({});
  const [business, setBusiness] = useState<Business | null>(null);
  const [addressKey, setAddressKey] = useState(0);
  
  // Shared customer creation dialog handler
  const handleCustomerCreated = (customer: Customer) => {
    setSuccess('Customer created');
    setError(null);
    loadCustomers();
  };
  
  // Job creation dialog state
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [selectedCustomerForJob, setSelectedCustomerForJob] = useState<Customer | null>(null);
  const [jobFormData, setJobFormData] = useState<CreateJobDto>({
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    jobAddress: '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium,
    notes: ''
  });
  
  // Customer search for job dialog
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  
  // Menu state for mobile actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomerForMenu, setSelectedCustomerForMenu] = useState<Customer | null>(null);

  // Validation functions
  const validateFirstName = (value: string): string | null => {
    if (!value.trim()) {
      return 'First name is required';
    }
    if (value.length > 100) {
      return 'First name cannot be longer than 100 characters';
    }
    return null;
  };

  const validateLastName = (value: string): string | null => {
    if (value && value.length > 100) {
      return 'Last name cannot be longer than 100 characters';
    }
    return null;
  };

  const validateEmail = (value: string): string | null => {
    if (value && value.trim()) {
      if (!/\S+@\S+\.\S+/.test(value)) {
        return 'Email is invalid';
      }
      if (value.length > 256) {
        return 'Email cannot be longer than 256 characters';
      }
    }
    return null;
  };

  const validatePhoneNumber = (value: string): string | null => {
    if (value && value.length > 20) {
      return 'Phone number cannot be longer than 20 characters';
    }
    return null;
  };

  const validateSource = (value: CustomerSource): string | null => {
    if (!value) {
      return 'Source is required';
    }
    return null;
  };

  const validateField = (field: string, value: string | CustomerSource): string | null => {
    switch (field) {
      case 'firstName':
        return validateFirstName(value as string);
      case 'lastName':
        return validateLastName(value as string);
      case 'email':
        return validateEmail(value as string);
      case 'phoneNumber':
        return validatePhoneNumber(value as string);
      case 'source':
        return validateSource(value as CustomerSource);
      default:
        return null;
    }
  };

  const handleFieldBlur = (field: string) => {
    const value = formData[field as keyof CreateCustomerDto] || '';
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error || ''
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    const firstNameError = validateFirstName(formData.firstName);
    if (firstNameError) errors.firstName = firstNameError;
    
    const lastNameError = validateLastName(formData.lastName || '');
    if (lastNameError) errors.lastName = lastNameError;
    
    const emailError = validateEmail(formData.email || '');
    if (emailError) errors.email = emailError;
    
    const phoneError = validatePhoneNumber(formData.phoneNumber || '');
    if (phoneError) errors.phoneNumber = phoneError;
    
    const sourceError = validateSource(formData.source);
    if (sourceError) errors.source = sourceError;
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    loadCustomers();
  }, [searchDto, user?.businessId]);

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsOffline(!customEvent.detail.isOnline);
      
      // When coming back online, refresh data
      if (customEvent.detail.isOnline) {
        loadCustomers();
      }
    };

    // Listen for offline/online events
    window.addEventListener('connectivity-changed', handleConnectivityChange);
    
    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
    };
  }, []);

  useEffect(() => {
    if (user?.businessId) {
      loadBusiness();
    }
  }, [user?.businessId]);

  // Update address key when business data changes to force re-render
  useEffect(() => {
    if (business) {
      setAddressKey(prev => prev + 1);
    }
  }, [business?.defaultCountry, business?.defaultCity]);

  // Debounced main customer search
  useEffect(() => {
    if (mainSearchTimeout) {
      clearTimeout(mainSearchTimeout);
    }

    const timeout = setTimeout(() => {
      setSearchDto(prev => ({
        ...prev,
        searchTerm: localSearchTerm || undefined,
        page: 1
      }));
      setPage(0);
    }, 300); // 300ms debounce
    
    setMainSearchTimeout(timeout);

    return () => {
      clearTimeout(timeout);
    };
  }, [localSearchTerm]);

  // Debounced search effect for job dialog
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (customerSearchTerm.trim()) {
      const timeout = setTimeout(() => {
        handleSearchCustomers();
      }, 300); // 300ms debounce
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [customerSearchTerm]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadBusiness = async () => {
    if (!user?.businessId) return;
    
    try {
      const businessData = await businessService.getBusiness(user.businessId);
      setBusiness(businessData);
    } catch (err: any) {
      console.error('Failed to load business:', err);
    }
  };

  const loadCustomers = async () => {
    // Don't load customers if user is not authenticated or businessId is not available
    if (!user?.businessId) {
      setCustomers([]);
      setTotalCount(0);
      setLoading(false);
      setIsInitialLoad(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Check if we're offline
      const isCurrentlyOffline = !offlineService.getOnlineStatus();
      
      if (isCurrentlyOffline) {
        console.log('[CustomersPage] Loading customers from IndexedDB (offline mode)');
        
        // Load customers from IndexedDB
        const customersData = await indexedDbService.getCustomersForBusiness(user.businessId);
        
        // Apply local filtering if search criteria provided
        let filteredCustomers = customersData;
        
        if (searchDto.searchTerm) {
          const searchTerm = searchDto.searchTerm.toLowerCase();
          filteredCustomers = customersData.filter(customer => 
            customer.firstName?.toLowerCase().includes(searchTerm) ||
            customer.lastName?.toLowerCase().includes(searchTerm) ||
            customer.email?.toLowerCase().includes(searchTerm) ||
            customer.phoneNumber?.includes(searchTerm) ||
            customer.address?.toLowerCase().includes(searchTerm)
          );
        }
        
        if (searchDto.statuses && searchDto.statuses.length > 0) {
          filteredCustomers = filteredCustomers.filter(customer => 
            searchDto.statuses!.includes(customer.status as CustomerStatus)
          );
        }
        
        // Sort customers by first name, then last name
        filteredCustomers = filteredCustomers.sort((a, b) => {
          const firstNameA = (a.firstName || '').toLowerCase();
          const firstNameB = (b.firstName || '').toLowerCase();
          const lastNameA = (a.lastName || '').toLowerCase();
          const lastNameB = (b.lastName || '').toLowerCase();
          
          // First sort by first name
          if (firstNameA !== firstNameB) {
            return firstNameA.localeCompare(firstNameB);
          }
          
          // If first names are the same, sort by last name
          return lastNameA.localeCompare(lastNameB);
        });
        
        // Apply pagination locally
        const startIndex = ((searchDto.page || 1) - 1) * (searchDto.pageSize || 20);
        const endIndex = startIndex + (searchDto.pageSize || 20);
        const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);
        
        setCustomers(paginatedCustomers);
        setTotalCount(filteredCustomers.length);
      } else {
        // Online mode - use API
        const searchParams = {
          ...searchDto
        };
        
        const [customersData, count] = await Promise.all([
          customerService.getCustomers(searchParams),
          customerService.getCustomerCount(searchParams)
        ]);
        setCustomers(customersData);
        setTotalCount(count);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleSearch = (field: keyof CustomerSearchDto, value: any) => {
    setSearchDto(prev => ({
      ...prev,
      [field]: value === '' || value === null ? undefined : value,
      page: 1
    }));
    setPage(0);
  };

  const handleStatusChange = (statuses: CustomerStatus[]) => {
    setSelectedStatuses(statuses);
    setSearchDto(prev => ({
      ...prev,
      statuses: statuses.length > 0 ? statuses : undefined,
      page: 1
    }));
    setPage(0);
  };


  const handleAddressChange = (address: string, latitude?: number, longitude?: number) => {
    setFormData(prev => ({
      ...prev,
      address
    }));
    setAddressCoordinates({ lat: latitude, lng: longitude });
  };

  const handleCreateJob = async () => {
    try {
      await jobService.createJob(jobFormData);
      setIsJobDialogOpen(false);
      setSelectedCustomerForJob(null);
      setJobFormData({
        customerId: '',
        assignedSalesPersonId: user?.id || '',
        jobAddress: '',
        type: JobType.Consultation,
        estimatedValue: 0,
        priority: Priority.Medium,
        notes: ''
      });
      setSuccess('Job created');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to create job');
      setSuccess(null);
    }
  };

  const handleSearchCustomers = async () => {
    if (!customerSearchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const searchDto: CustomerSearchDto = {
        searchTerm: customerSearchTerm,
        page: 1,
        pageSize: 20
      };
      const results = await customerService.getCustomers(searchDto);
      setSearchResults(results);
    } catch (err: any) {
      setError(err.message || 'Failed to search customers');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomerForJob(customer);
    setJobFormData(prev => ({
      ...prev,
      customerId: customer.id,
      jobAddress: customer.address || ''
    }));
    setShowCustomerSearch(false);
    setSearchResults([]);
    setCustomerSearchTerm('');
  };

  const handleFieldFocus = () => {
    if (showCustomerSearch) {
      setShowCustomerSearch(false);
      setSearchResults([]);
      setCustomerSearchTerm('');
    }
  };

  // Menu handlers for mobile actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomerForMenu(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomerForMenu(null);
  };

  const handleMenuAction = (action: string) => {
    if (!selectedCustomerForMenu) return;
    
    switch (action) {
      case 'viewJobs':
        handleViewJobs(selectedCustomerForMenu);
        break;
      case 'createJob':
        handleCreateJobForCustomer(selectedCustomerForMenu);
        break;
      case 'edit':
        handleEditClick(selectedCustomerForMenu);
        break;
      case 'delete':
        handleDeleteCustomer(selectedCustomerForMenu.id);
        break;
      case 'reinstate':
        handleReinstateCustomer(selectedCustomerForMenu);
        break;
    }
    handleMenuClose();
  };


  const resetEditForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: '',
      city: '',
      country: '',
      source: CustomerSource.Other
    });
    setAddressCoordinates({});
    setValidationErrors({});
    setError(null);
    setSuccess(null);
    setSelectedCustomer(null);
  };

  const resetJobForm = () => {
    setJobFormData({
      customerId: '',
      assignedSalesPersonId: user?.id || '',
      jobAddress: '',
      type: JobType.Consultation,
      estimatedValue: 0,
      priority: Priority.Medium,
      notes: ''
    });
    setSelectedCustomerForJob(null);
    setCustomerSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
    setShowCustomerSearch(false);
    setError(null);
    setSuccess(null);
  };


  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Clean up form data - convert empty strings to null for optional fields
      const cleanedFormData = {
        ...formData,
        address: formData.address || undefined,
        city: formData.city || undefined,
        country: formData.country || undefined,
        secondaryContactName: formData.secondaryContactName || undefined,
        secondaryContactPhone: formData.secondaryContactPhone || undefined,
        secondaryContactEmail: formData.secondaryContactEmail || undefined,
        preferredCommunicationMethod: formData.preferredCommunicationMethod || undefined,
        notes: formData.notes || undefined
      };
      
      const updateDto: UpdateCustomerDto = {
        id: selectedCustomer.id,
        ...cleanedFormData
      };
      await customerService.updateCustomer(updateDto);
      setIsEditDialogOpen(false);
      setSelectedCustomer(null);
      setValidationErrors({});
      setSuccess('Customer updated successfully');
      setError(null);
      loadCustomers();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        // Handle validation errors
        const serverValidationErrors = err.response.data.errors;
        const errorMessages = Object.entries(serverValidationErrors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`);
        
        if (errorMessages.length === 1) {
          setError(errorMessages[0]);
        } else {
          setError('Please fix the following errors:');
        }
      } else {
        setError(err.message || 'Failed to update customer');
      }
      setSuccess(null);
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (!window.confirm('Are you sure you want to mark this customer as inactive? This will remove them from the active customer list.')) return;
    
    try {
      await customerService.deleteCustomer(customerId);
      setSuccess('Customer marked as inactive successfully');
      setError(null);
      loadCustomers();
    } catch (err: any) {
      setError(err.message || 'Failed to mark customer as inactive');
    }
  };

  const handleReinstateCustomer = async (customer: Customer) => {
    if (!window.confirm(`Are you sure you want to reinstate ${customer.fullName}? This will mark them as active again.`)) return;
    
    try {
      const updateDto: UpdateCustomerDto = {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phoneNumber: customer.phoneNumber,
        address: customer.address,
        city: customer.city,
        country: customer.country,
        source: customer.source,
        status: CustomerStatus.Active,
        preferredCommunicationMethod: customer.preferredCommunicationMethod,
        notes: customer.notes
      };
      
      await customerService.updateCustomer(updateDto);
      setSuccess('Customer reinstated successfully');
      setError(null);
      loadCustomers();
    } catch (err: any) {
      setError(err.message || 'Failed to reinstate customer');
    }
  };

  const handleEditClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData({
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      phoneNumber: customer.phoneNumber || '',
      address: customer.address || '',
      city: customer.city || '',
      country: customer.country || '',
      source: customer.source,
      preferredCommunicationMethod: customer.preferredCommunicationMethod || '',
      notes: customer.notes || ''
    });
    setAddressCoordinates({}); // Reset coordinates when editing
    setValidationErrors({});
    setIsEditDialogOpen(true);
  };

  const handleCreateJobForCustomer = (customer: Customer) => {
    setSelectedCustomerForJob(customer);
    setJobFormData({
      customerId: customer.id,
      assignedSalesPersonId: user?.id || '',
      jobAddress: customer.address || '',
      type: JobType.Consultation,
      estimatedValue: 0,
      priority: Priority.Medium,
      notes: ''
    });
    setShowCustomerSearch(false);
    setError(null);
    setSuccess(null);
    setIsJobDialogOpen(true);
  };

  const handleViewJobs = (customer: Customer) => {
    navigate(`/crm/jobs?customerId=${customer.id}`);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSearchDto(prev => ({
      ...prev,
      page: newPage + 1
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSearchDto(prev => ({
      ...prev,
      page: 1,
      pageSize: newRowsPerPage
    }));
  };

  if (isInitialLoad) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header skeleton */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Skeleton variant="text" width={150} height={40} />
          <Skeleton variant="rectangular" width={150} height={36} />
        </Box>

        {/* Search and Filters skeleton */}
        <Card sx={{ mb: 3, backgroundColor: theme.accentColor }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Skeleton variant="rectangular" width="100%" height={56} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Table skeleton */}
        <TableContainer component={Paper} sx={{ backgroundColor: theme.accentColor }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Skeleton variant="circular" width={18} height={18} />
                      <Skeleton variant="text" width={150} height={24} />
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Skeleton variant="text" width={200} height={20} />
                      <Skeleton variant="text" width={180} height={20} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <>

      <Helmet>
        <title>Customers | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1">
              Customers
            </Typography>
          </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={isOffline}
          title={isOffline ? "Customer creation is not available offline" : "Add Customer"}
        >
          Add Customer
        </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 3, backgroundColor: theme.accentColor }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search customers..."
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={selectedStatuses}
                    onChange={(e) => handleStatusChange(e.target.value as CustomerStatus[])}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    label="Status"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((status) => (
                          <Chip
                            key={status}
                            label={customerService.getStatusDisplayName(status)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(CustomerStatus).filter(v => typeof v === 'number').map((status) => (
                      <MenuItem key={status} value={status}>
                        <Checkbox checked={selectedStatuses.indexOf(status as CustomerStatus) > -1} />
                        <ListItemText primary={customerService.getStatusDisplayName(status as CustomerStatus)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <TableContainer component={Paper} sx={{ backgroundColor: theme.accentColor }}>
          {loading && (
            <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
              <Box sx={{ width: '100%', height: 4, bgcolor: 'primary.main', opacity: 0.7 }}>
                <Box
                  sx={{
                    height: '100%',
                    width: '30%',
                    bgcolor: 'primary.light',
                    animation: 'loading 1s ease-in-out infinite',
                    '@keyframes loading': {
                      '0%': { marginLeft: '0%' },
                      '50%': { marginLeft: '70%' },
                      '100%': { marginLeft: '0%' }
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show skeleton rows while loading
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={18} height={18} />
                        <Skeleton variant="text" width={150} height={24} />
                      </Box>
                      <Box sx={{ mt: 0.5 }}>
                        <Skeleton variant="text" width={200} height={20} />
                        <Skeleton variant="text" width={180} height={20} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Alert severity="info" sx={{ my: 2 }}>
                      No customers found. {searchDto.searchTerm || selectedStatuses.length > 0 ? 'Try adjusting your search filters.' : 'Click "Add Customer" to create your first customer.'}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id} hover>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <Tooltip title={customer.statusDisplayName}>
                          {customer.status === CustomerStatus.Active || customer.status === 'Active' ? (
                            <CheckCircleIcon sx={{ fontSize: 18, mr: 1, color: 'grey.500' }} />
                          ) : customer.status === CustomerStatus.Prospect || customer.status === 'Prospect' ? (
                            <HelpOutlineIcon sx={{ fontSize: 18, mr: 1, color: 'grey.500' }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 18, mr: 1, color: 'grey.500' }} />
                          )}
                        </Tooltip>
                        <Typography variant="body2" fontWeight="medium">
                          {customer.fullName}
                        </Typography>
                      </Box>
                      {customer.email && (
                        <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {customer.email}
                          </Typography>
                        </Box>
                      )}
                      {customer.phoneNumber && (
                        <Box display="flex" alignItems="center">
                          <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {customer.phoneNumber}
                          </Typography>
                        </Box>
                      )}
                      {customer.address && (
                        <Box display="flex" alignItems="center">
                          <PinDrop sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {customer.address}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {/* Desktop Actions */}
                    <Box display={{ xs: 'none', sm: 'flex' }} gap={1} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => handleViewJobs(customer)}
                        color="primary"
                        title="View Jobs"
                      >
                        <AssignmentIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCreateJobForCustomer(customer)}
                        color="primary"
                        title="Create Job"
                        disabled={isOffline}
                      >
                        <AddIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(customer)}
                        color="primary"
                        title="Edit Customer"
                        disabled={isOffline}
                      >
                        <EditIcon />
                      </IconButton>
                      {customer.status === CustomerStatus.Inactive || customer.status === 'Inactive' ? (
                        <IconButton
                          size="small"
                          onClick={() => handleReinstateCustomer(customer)}
                          color="success"
                          title="Mark as Active"
                          disabled={isOffline}
                        >
                          <RestoreIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCustomer(customer.id)}
                          color="error"
                          title="Mark as Inactive"
                          disabled={isOffline}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    
                    {/* Mobile Actions - 3 dots menu */}
                    <Box display={{ xs: 'block', sm: 'none' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, customer)}
                        color="primary"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>

        {/* Create Customer Dialog */}
        <CustomerFormDialog
          open={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSuccess={handleCustomerCreated}
          business={business}
          title="Create Customer"
        />

        {/* Edit Customer Dialog */}
        <BaseDialog 
          open={isEditDialogOpen} 
          onClose={() => {
            setIsEditDialogOpen(false);
            resetEditForm();
          }}
          title="Edit Customer"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
            }
          }}
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button 
                onClick={() => {
                  setIsEditDialogOpen(false);
                  resetEditForm();
                }}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleCreateJobForCustomer(selectedCustomer!)}
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!selectedCustomer}
              >
                Create Job
              </Button>
              <Button onClick={handleUpdateCustomer} variant="contained">Update</Button>
            </>
          }
        >
            {error && (
              <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                {error.includes('Please fix the following errors:') ? (
                  <div>
                    <div>{error}</div>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {Object.entries(validationErrors).map(([field, message]) => (
                        <li key={field}>{message}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  error
                )}
              </Alert>
            )}
            {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  onBlur={() => handleFieldBlur('firstName')}
                  error={!!validationErrors.firstName}
                  helperText={validationErrors.firstName}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  onBlur={() => handleFieldBlur('lastName')}
                  error={!!validationErrors.lastName}
                  helperText={validationErrors.lastName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  onBlur={() => handleFieldBlur('email')}
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  onBlur={() => handleFieldBlur('phoneNumber')}
                  error={!!validationErrors.phoneNumber}
                  helperText={validationErrors.phoneNumber}
                />
              </Grid>
              <Grid item xs={12}>
                <GoogleMapsAutocomplete
                  key={`address-create-${addressKey}`}
                  value={formData.address || ''}
                  onChange={handleAddressChange}
                  label="Address"
                  defaultBounds={{
                    city: business?.defaultCity,
                    country: business?.defaultCountry
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={formData.source}
                    onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as CustomerSource }))}
                    onBlur={() => handleFieldBlur('source')}
                    label="Source"
                    error={!!validationErrors.source}
                  >
                    {Object.values(CustomerSource).filter(v => typeof v === 'number').map((source) => (
                      <MenuItem key={source} value={source}>
                        {customerService.getSourceDisplayName(source as CustomerSource)}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.source && (
                    <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                      {validationErrors.source}
                    </div>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
        </BaseDialog>

        {/* Create Job Dialog */}
        <BaseDialog 
          open={isJobDialogOpen} 
          onClose={() => {
            setIsJobDialogOpen(false);
            resetJobForm();
          }}
          title={selectedCustomerForJob?.fullName?.trim() ? `Create Job for ${selectedCustomerForJob.fullName}` : 'Create Job'}
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
            }
          }}
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button 
                onClick={() => {
                  setIsJobDialogOpen(false);
                  resetJobForm();
                }}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleCreateJob} variant="contained">Create Job</Button>
            </>
          }
        >
            {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Selected Customer Display - Always at top */}
              {selectedCustomerForJob && (
                <Grid item xs={12}>
                  <CustomerDetailsCard
                    customer={selectedCustomerForJob}
                    showChangeButton={false}
                  />
                </Grid>
              )}

              <JobForm
                formData={jobFormData}
                onFormDataChange={setJobFormData}
                defaultBounds={{
                  city: business?.defaultCity,
                  country: business?.defaultCountry
                }}
                onFieldFocus={handleFieldFocus}
                showCustomerSelection={false}
              />
            </Grid>
        </BaseDialog>

        {/* Mobile Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleMenuAction('viewJobs')}>
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            View Jobs
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuAction('createJob')}
            disabled={isOffline}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            Create Job
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuAction('edit')}
            disabled={isOffline}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit Customer
          </MenuItem>
          {selectedCustomerForMenu && (selectedCustomerForMenu.status === CustomerStatus.Inactive || selectedCustomerForMenu.status === 'Inactive') ? (
            <MenuItem 
              onClick={() => handleMenuAction('reinstate')}
              disabled={isOffline}
            >
              <ListItemIcon>
                <RestoreIcon fontSize="small" />
              </ListItemIcon>
              Mark as Active
            </MenuItem>
          ) : (
            <MenuItem 
              onClick={() => handleMenuAction('delete')}
              disabled={isOffline}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              Mark as Inactive
            </MenuItem>
          )}
        </Menu>

      </Box>

    </>
  );
};

export default CustomersPage;
